import React, {useEffect, useState, useRef, useContext} from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../../assets/stylesheets/header.css";
import logo from "../../assets/image/logo.svg";
import phoneIcon from "../../assets/image/phone.svg";
import gmailIcon from "../../assets/image/gmail.svg";
import {
    FaFacebookF,
    FaInstagram,
    FaTelegramPlane,
    FaWhatsapp,
    FaBars,
    FaTimes,
    FaSearch,
    FaAngleDown,
    FaHeart, FaStar,
    FaPhone, FaFacebookMessenger,
} from "react-icons/fa";
import axios from "axios";
import { routes } from "../../routes";
import DOMPurify from "dompurify";
import Modal from "react-modal";
import {ContactInfoContext} from "../additional/ContactInfoContext";

function Header() {
    const { t, i18n } = useTranslation(["home", "about", "blog", "faq", "choose", "legal", "ourspace", "seales", "freeconsult"]);
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [isSearch, setIsSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const { contactInfo } = useContext(ContactInfoContext);

    const wrapperRef = useRef();
    const searchRef = useRef();

    // Structured data for SEO
    const myPerson = {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        "name": "SIEVN",
        "image": "https://www.sievn.am/static/media/logo.1c8cd2b3ece6ae5dda6987b2be02ac51.svg",
        "@id": "",
        "url": "https://www.sievn.am/",
        "telephone": contactInfo.telPhone || "(+374) 33 30 30 11",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Nikoghayos Adonts st 6",
            "addressLocality": "Yerevan",
            "postalCode": "0014",
            "addressCountry": "AM"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": 40.212504182261554,
            "longitude": 44.52409444064555
        },
        "sameAs": [
            contactInfo.facebookUrl || "https://www.facebook.com/sievnrealestate/?ref=pages_you_manage",
            contactInfo.instagramUrl || "https://www.instagram.com/sievn_real_estate_agency?igshid=YmMyMTA2M2Y%3D",
            "https://www.sievn.am/"
        ]
    };

    const StructuredData = () => (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(myPerson)) }}
        />
    );

    const [currentLang, setCurrentLang] = useState(i18n.language);

    // When URL changes, update state & i18n.language if needed
    useEffect(() => {
        const match = location.pathname.match(/^\/(en|ru|hy)(\/|$)/);
        const urlLang = match?.[1];

        if (urlLang && urlLang !== currentLang) {
            setCurrentLang(urlLang);
            if (i18n.language !== urlLang) {
                i18n.changeLanguage(urlLang);
            }
        }
    }, [location.pathname, i18n, currentLang]);

    const changeLanguage = (newLang) => {
        if (newLang === currentLang) return;

        setCurrentLang(newLang);
        i18n.changeLanguage(newLang);

        const match = location.pathname.match(/^\/(en|ru|hy)\/([^\/]+)(\/.*)?$/);

        if (!match) {
            navigate(`/${newLang}`, { replace: true });
            return;
        }
        const [, oldLang, oldTranslatedSegment, rest = ""] = match;
        let routeKey = null;

        for (const [key, translations] of Object.entries(routes)) {
            if (translations[oldLang] === oldTranslatedSegment) {
                routeKey = key;
                break;
            }
        }

        if (!routeKey) {
            navigate(`/${newLang}`, { replace: true });
            return;
        }

        const newTranslatedSegment = routes[routeKey][newLang];
        const newPath = `/${newLang}/${newTranslatedSegment}${rest}`;
        navigate(newPath, { replace: true });
    };

    // Handle outside click to close menu and search
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                searchRef.current && !searchRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setIsSearch(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/${i18n.language}/${t("estate:search-url")}?query=${searchQuery}`);
            setIsSearch(false);
            setSearchQuery("");
        }
    };

    // Define dropdown items for each menu (currently empty except for Learn More)
    const homeDropdownItems = []; // Add items here to enable dropdown
    const aboutDropdownItems = []; // Add items here to enable dropdown
    const blogDropdownItems = []; // Add items here to enable dropdown
    const contactDropdownItems = []; // Add items here to enable dropdown
    const learnMoreDropdownItems = [
        { to: `/${i18n.language}/${t("choose:whyChooseUs")}`, label: t("choose:chooseUs") },
        { to: `/${i18n.language}/${t("ourspace:ourspace-url")}`, label: t("ourspace:ourspace") },
        { to: `/${i18n.language}/${t("faq:faq-url")}`, label: t("faq:faq") },
        { to: `/${i18n.language}/${t("seales:sealesProces")}`, label: t("seales:salestext") },
        { to: `/${i18n.language}/${t("legal:legalservice")}`, label: t("legal:attornay") },
        { to: `/${i18n.language}/${t("freeconsult:freeCons")}`, label: t("freeconsult:freeConsult") },
    ];

    return (
        <>
            <StructuredData />
            <header className="header">
                <div className="header__container">
                    <div className="header__top">
                        <Link to={`/${i18n.language}`} className="header__logo">
                            <img src={logo} alt="SIEVN Logo" title="SIEVN" className="header__logo-img" />
                        </Link>
                        <div className="header__actions">
                            <div className="header__search" ref={searchRef}>
                                <button
                                    className="header__search-toggle"
                                    onClick={() => setIsSearch(!isSearch)}
                                >
                                    <FaSearch size={20} />
                                </button>
                                <div className={`header__search-bar ${isSearch ? "header__search-bar--active" : ""}`}>
                                    <form onSubmit={handleSearch}>
                                        <input
                                            type="text"
                                            className="header__search-input"
                                            placeholder={t("search")}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </form>
                                </div>
                            </div>
                            <button className="header__favorites-toggle">
                                <Link
                                    to={`/${i18n.language}/${t("estate:favorites-url")}`}
                                    onClick={() => setIsOpen(false)}
                                >
                                    <FaStar style={{ color: "#115772" }} />
                                </Link>
                            </button>
                            <select
                                className="header__lang"
                                value={currentLang}
                                onChange={(e) => changeLanguage(e.target.value)}
                            >
                                <option value="hy">ՀԱՅ</option>
                                <option value="en">ENG</option>
                                <option value="ru">РУС</option>
                            </select>
                            <button
                                className="header__burger"
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                            </button>
                        </div>
                    </div>
                    <nav className={`header__nav ${isOpen ? "header__nav--active" : ""}`} ref={wrapperRef}>
                        <div className="header__nav-inner">
                        <ul className="header__nav-list">
                            <li className="header__nav-item">
                                {/* To enable dropdown for Home, add items to homeDropdownItems array */}
                                {/* Example:
                                    const homeDropdownItems = [
                                        { to: `/${i18n.language}/featured`, label: t("featuredProperties") },
                                        { to: `/${i18n.language}/testimonials`, label: t("testimonials") },
                                    ];
                                    Then uncomment the header__dropdown and header__dropdown-menu below
                                */}
                                <Link
                                    to={`/${i18n.language}`}
                                    className="header__nav-link"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {t("home")}
                                    {homeDropdownItems.length > 0 && <FaAngleDown className="header__dropdown-icon" />}
                                </Link>
                                {/* {homeDropdownItems.length > 0 && (
                                    <div className="header__dropdown">
                                        <ul className="header__dropdown-menu">
                                            {homeDropdownItems.map((item, index) => (
                                                <li key={index} className="header__dropdown-item">
                                                    <Link
                                                        to={item.to}
                                                        className="header__dropdown-link"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )} */}
                            </li>
                            <li className="header__nav-item">
                                {/* To enable dropdown for About, add items to aboutDropdownItems array */}
                                {/* Example:
                                    const aboutDropdownItems = [
                                        { to: `/${i18n.language}/team`, label: t("about:team") },
                                        { to: `/${i18n.language}/mission`, label: t("about:mission") },
                                    ];
                                */}
                                <Link
                                    to={`/${i18n.language}/${t("about:aboutUs-url")}`}
                                    className="header__nav-link"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {t("about:about")}
                                    {aboutDropdownItems.length > 0 && <FaAngleDown className="header__dropdown-icon" />}
                                </Link>
                                {/* {aboutDropdownItems.length > 0 && (
                                    <div className="header__dropdown">
                                        <ul className="header__dropdown-menu">
                                            {aboutDropdownItems.map((item, index) => (
                                                <li key={index} className="header__dropdown-item">
                                                    <Link
                                                        to={item.to}
                                                        className="header__dropdown-link"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )} */}
                            </li>
                            <li className="header__nav-item">
                                {/* To enable dropdown for Blog, add items to blogDropdownItems array */}
                                {/* Example:
                                    const blogDropdownItems = [
                                        { to: `/${i18n.language}/blog/news`, label: t("blog:news") },
                                        { to: `/${i18n.language}/blog/tips`, label: t("blog:tips") },
                                    ];
                                */}
                                <Link
                                    to={`/${i18n.language}/${t("blog:blog-url")}`}
                                    className="header__nav-link"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {t("blog:blog")}
                                    {blogDropdownItems.length > 0 && <FaAngleDown className="header__dropdown-icon" />}
                                </Link>
                                {/* {blogDropdownItems.length > 0 && (
                                    <div className="header__dropdown">
                                        <ul className="header__dropdown-menu">
                                            {blogDropdownItems.map((item, index) => (
                                                <li key={index} className="header__dropdown-item">
                                                    <Link
                                                        to={item.to}
                                                        className="header__dropdown-link"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )} */}
                            </li>
                            <li className="header__nav-item">
                                {/* To enable dropdown for Contact, add items to contactDropdownItems array */}
                                {/* Example:
                                    const contactDropdownItems = [
                                        { to: `/${i18n.language}/support`, label: t("support") },
                                        { to: `/${i18n.language}/feedback`, label: t("feedback") },
                                    ];
                                */}
                                <Link
                                    to={`/${i18n.language}/contacts`}
                                    className="header__nav-link"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {t("contact")}
                                    {contactDropdownItems.length > 0 && <FaAngleDown className="header__dropdown-icon" />}
                                </Link>
                                {/* {contactDropdownItems.length > 0 && (
                                    <div className="header__dropdown">
                                        <ul className="header__dropdown-menu">
                                            {contactDropdownItems.map((item, index) => (
                                                <li key={index} className="header__dropdown-item">
                                                    <Link
                                                        to={item.to}
                                                        className="header__dropdown-link"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )} */}
                            </li>
                            <li className="header__nav-item header__dropdown">
                                <div className="header__nav-link header__dropdown-toggle">
                                    {t("learnMore")} {learnMoreDropdownItems.length > 0 && <FaAngleDown className="header__dropdown-icon" />}
                                </div>
                                {learnMoreDropdownItems.length > 0 && (
                                    <ul className="header__dropdown-menu">
                                        {learnMoreDropdownItems.map((item, index) => (
                                            <li key={index} className="header__dropdown-item">
                                                <Link
                                                    to={item.to}
                                                    className="header__dropdown-link"
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    {item.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        </ul>

                        <div className="header__contact">
                            {/*<div className="header__contact-item">*/}
                            {/*    <img src={phoneIcon} alt="Phone" className="header__contact-icon" />*/}
                            {/*    <a href={`tel:${contactInfo.telPhone}`} className="header__contact-link">*/}
                            {/*        {contactInfo.telPhone || "(+374) 33 30 30 11"}*/}
                            {/*    </a>*/}
                            {/*</div>*/}
                            {/*<div className="header__contact-item">*/}
                            {/*    <img src={gmailIcon} alt="Email" className="header__contact-icon" />*/}
                            {/*    <a href={`mailto:${contactInfo.email}`} className="header__contact-link">*/}
                            {/*        {contactInfo.email || "info@sievn.am"}*/}
                            {/*    </a>*/}
                            {/*</div>*/}
                            <div className="header__social">

                                {/*{contactInfo.email && (*/}
                                {/*    <a href={`mailto:${contactInfo.email}`} target="_blank" rel="noopener noreferrer"*/}
                                {/*       className="header__social-icon">*/}
                                {/*        <img src={gmailIcon} alt="Phone" />*/}
                                {/*    </a>*/}
                                {/*)}*/}

                                {contactInfo.telPhone2 && (
                                    <a href={`tel:${contactInfo.telPhone2}`} className="header__socialtext" >
                                        {contactInfo.telPhone2}
                                    </a>
                                )}

                                {contactInfo.wpPhone && (
                                    <a href={`https://api.whatsapp.com/send?phone=${contactInfo.wpPhone}`}
                                       target="_blank" rel="noopener noreferrer" className="header__social-icon">
                                        <FaWhatsapp />
                                    </a>
                                )}

                                {contactInfo.facebookUrl && (
                                    <a href={contactInfo.facebookUrl} target="_blank" rel="noopener noreferrer"
                                       className="header__social-icon">
                                        <FaFacebookF />
                                    </a>
                                )}
                                {contactInfo.instagramUrl && (
                                    <a href={contactInfo.instagramUrl} target="_blank" rel="noopener noreferrer"
                                       className="header__social-icon">
                                        <FaInstagram />
                                    </a>
                                )}


                                {contactInfo.tgUsername && (
                                    <a href={`https://t.me/${contactInfo.tgUsername}`} target="_blank"
                                       rel="noopener noreferrer" className="header__social-icon">
                                        <FaTelegramPlane />
                                    </a>
                                )}

                                {contactInfo.messenger && (
                                    <a
                                        href={contactInfo.messenger}
                                        target="_blank"
                                        className="header__social-icon"
                                        rel="noopener noreferrer"
                                    >
                                        <FaFacebookMessenger/>
                                    </a>
                                )}

                            </div>
                            <select
                                className="header__lang-active"
                                value={currentLang}
                                onChange={(e) => changeLanguage(e.target.value)}
                            >
                                <option value="hy">ՀԱՅ</option>
                                <option value="en">ENG</option>
                                <option value="ru">РУС</option>
                            </select>
                        </div>
                        </div>
                    </nav>
                </div>
            </header>
        </>
    );
}

export default Header;